import React, {FunctionComponent, useEffect, useState} from "react"
import {useModal} from "@murphy-frontend/web-core/contexts/ModalContext"
import {ModalConfiguration} from "@murphy-frontend/web-core/components/Modal"
import {
	DroneCheckListItemType,
	DroneComponentCheckList,
	DroneComponentCheckListItem
} from "@murphy-frontend/common/api/DroneEvents/types"
import {useParams} from "react-router-dom"
import {useDroneEvent, useSaveEventField} from "@murphy-frontend/common/api/DroneEvents/Queries"
import {MurphyOpenedDroneEventDto} from "@murphy-frontend/common/interfaces/IDroneEventsApi"
import Textarea from "../FormFields/Textarea"
import Checkbox from "../FormFields/Checkbox"
import EventHistoryTable from "../EventHistoryTable"
import {useUserPreferences} from "../../../../common/contexts/UserPreferencesContext"

interface CheckListProps {
	checkList: DroneComponentCheckList
}

const CheckList: FunctionComponent<CheckListProps> = ({checkList: eventFlowBlock}) => {
	const [isLoading, setIsLoading] = useState(false)
	const [isSaved, setIsSaved] = useState(false)
	const [eventData, setEventData] = useState<MurphyOpenedDroneEventDto>()
	const {id} = useParams()
	const {timeZone, translations} = useUserPreferences()
	const {openModal, closeModal} = useModal()
	const {
		mutate: saveEvent,
		isPending: isSaveEventPending,
		isSuccess: isSaveEventSuccess
	} = useSaveEventField(parseInt(id))

	const {
		data: startedEventFlow,
		isLoading: startedEventFlowIsLoading,
		isPending: startedEventFlowIsPending
	} = useDroneEvent(parseInt(id))

	useEffect(() => {
		if (!startedEventFlowIsLoading) setEventData(startedEventFlow)
	}, [startedEventFlowIsPending, startedEventFlow, startedEventFlowIsLoading])

	useEffect(() => {
		setIsSaved(startedEventFlow.IsModified)
	}, [startedEventFlowIsLoading, startedEventFlowIsPending, startedEventFlow])

	useEffect(() => {
		if (isSaveEventPending) {
			setIsLoading(isSaveEventPending)
			setTimeout(() => {
				setIsLoading(false)
			}, 3000)
		}
	}, [isSaveEventPending])

	useEffect(() => {
		if (!isLoading && isSaveEventSuccess) {
			setIsSaved(true)
			setTimeout(() => {
				setIsSaved(false)
			}, 3000)
		}
	}, [isSaveEventSuccess, isLoading])

	const handleSubmit = (val: string, fieldId: string) => {
		saveEvent({
			droneId: parseInt(id),
			fieldId: parseInt(fieldId),
			updateValue: val
		})
	}

	const onClickShowHistory = () => {
		const modalConf: ModalConfiguration = {
			body: (
				<EventHistoryTable
					history={eventData.History}
					timeZone={timeZone}
					eventFlowHistory={eventData.StartedEventFlowHistory}
					translations={translations}
				/>
			),
			position: "center",
			buttonText: "Ok",
			hideButton: true
		}

		openModal(modalConf)
	}

	const getCheckListBasedOnType = (checkListItem: DroneComponentCheckListItem) => {
		const {__Type: __type} = checkListItem

		switch (__type) {
			case DroneCheckListItemType.TextInput:
				return (
					<Textarea
						title={checkListItem.Label}
						value={checkListItem.Value || ""}
						placeholder={checkListItem.Placeholder}
						fieldId={checkListItem.FieldId.toString()}
						onSubmit={handleSubmit}
						disabled={startedEventFlow.Completed}
					/>
				)
			case DroneCheckListItemType.Checkbox:
				return (
					<Checkbox
						title={checkListItem.Label}
						value={checkListItem.Value}
						fieldId={checkListItem.FieldId.toString()}
						onSubmit={handleSubmit}
						disabled={startedEventFlow.Completed}
					/>
				)
			case DroneCheckListItemType.Select:
				return <div>{checkListItem.Value}</div>
			default:
				return <input type='text' />
		}
	}

	return (
		<>
			<div className='event-block-title-wrap'>
				<div className='event-block-title'>{eventFlowBlock.Title}</div>
				{isLoading ? (
					<div className='event-block-saved'>Saving...</div>
				) : (
					<>
						{isSaved && (
							<div className='event-block-saved--table' onClick={() => onClickShowHistory()}>
								Saved
							</div>
						)}
					</>
				)}
			</div>
			{eventFlowBlock.Items.map(ef => (
				<div className='event-block-row' key={ef.FieldId}>
					{getCheckListBasedOnType(ef)}
				</div>
			))}
		</>
	)
}

export default CheckList
